import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import ResetButton from "../../../generalReport/controllers/components/ResetButton"
import ClientReportBurnchart from "./ClientReportBurnchart.controller"
import ClientReportBurnchartSummary from "./ClientReportBurnchartSummary.controller"
import useToast from "../../../../hooks/useToast.hook"
import {
  calculateBurnchartProjectionGeneric,
  calculateBurnchartProjectionFromPrecalculated,
  getPeriodNumberFromDate,
} from "../../../../utils/clientReport.utils"
import { getValidLuxonDate } from "../../../../utils/date.utils"
import {
  BURNCHART_ESTIMATION_DEFAULT_SPREAD,
  CLIENT_SPRINT_CARD_MODES,
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
  INITIAL_BURNCHART_ESTIMATED_SPRINTS,
  INITIAL_BURNCHART_STATE,
} from "../../../../consts/modules/clientReport.consts"
import { getClientReportBurnchartDataByClientIdAndPeriod } from "../../../../services/clientReport.service"
import useHighlightLineChartData from "../../../../hooks/graphics/useHighlightLineChartData.hook"
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"

const ClientBurnchart = ({
  selectedPeriod,
  period,
  refresh,
  mode = CLIENT_SPRINT_CARD_MODES.EDITABLE,
  refChart,
  isPresentationMode = false,
  collapsible = false,
  sprintData,
  extraData,
}) => {
  const [burnchartState, setBurnchartState] = useState(INITIAL_BURNCHART_STATE)
  const [prevBurnchartState, setPrevBurnchartState] = useState(
    INITIAL_BURNCHART_STATE
  )
  const [estimatedSprints, setEstimatedSprints] = useState(
    INITIAL_BURNCHART_ESTIMATED_SPRINTS
  )
  const [burnchartData, setBurnchartData] = useState([])
  const [lastSprint, setLastSprint] = useState(null)
  const [loading, setLoading] = useState(false)

  // Only use to demonstrate spread factor, remove after crosscheck
  const [sandboxSpread] = useState(BURNCHART_ESTIMATION_DEFAULT_SPREAD)
  const { setErrorMessage } = useToast()
  const sprintId =
    isPresentationMode ? selectedPeriod?.periodId : selectedPeriod?.id
  const clientId =
    isPresentationMode ?
      selectedPeriod?.clientData?.clientId
    : selectedPeriod?.clientId
  // Detectar si los datos son semanales o de sprints
  const isWeeklyData = useMemo(() => {
    if (!burnchartData || burnchartData.length === 0) return false
    return burnchartData[0]?.weekNumber !== undefined
  }, [burnchartData])

  const periodKey = isWeeklyData ? "weekNumber" : "sprintNumber"
  const averageKey =
    isWeeklyData ? "averagePointsPerWeek" : "averagePointsPerSprint"
  const shouldSkipProjection =
    (isPresentationMode &&
      sprintData?.semanasNominales != null &&
      sprintData?.semanasOptimistas != null &&
      sprintData?.semanasPesimistas != null) ||
    (!isPresentationMode &&
      extraData?.semanasNominales != null &&
      extraData?.semanasOptimistas != null &&
      extraData?.semanasPesimistas != null)

  const [milestoneForChart, setMilestoneForChart] = useState(null)

  useEffect(() => {
    if (!sprintId) return
    if (!isPresentationMode && !extraData?.semanasNominales) return
    fetchBurnchartData()
  }, [selectedPeriod, refresh, sprintId, clientId, extraData])
  useEffect(() => {
    if (shouldSkipProjection) return

    if (burnchartData && burnchartData.length > 0) {
      const realSprints = burnchartData.filter(
        (sprint) =>
          !Object.keys(sprint).includes(
            "projectedNominalTotalCompletedPoints"
          ) ||
          sprint.projectedNominalTotalCompletedPoints ===
            sprint.totalCompletedPoints
      )
      const lastSprint = realSprints[realSprints.length - 1]

      const milestonePeriods =
        milestoneForChart ?
          {
            nominal: milestoneForChart.nominalPeriod,
            optimistic: milestoneForChart.optimisticPeriod,
            pessimistic: milestoneForChart.pessimisticPeriod,
            totalPoints: milestoneForChart.totalPoints,
          }
        : null

      const {
        sprints: burnchartSprints,
        estimatedSprint,
        optimisticSprint,
        pessimisticSprint,
      } = calculateBurnchartProjectionGeneric({
        realPeriods: realSprints,
        lastPeriod: {
          ...lastSprint,
          [averageKey]: burnchartState[averageKey],
          totalPointsToComplete: burnchartState.totalPointsToComplete,
          totalCompletedPoints: burnchartState.totalCompletedPoints,
        },
        periodKey,
        averageKey,
        spread: sandboxSpread,
        milestonePeriods,
      })
      setBurnchartData(burnchartSprints)
      setEstimatedSprints({
        estimatedSprint,
        optimisticSprint,
        pessimisticSprint,
      })
    }
  }, [burnchartState, sandboxSpread, periodKey, averageKey, milestoneForChart])

  const fetchBurnchartData = async () => {
    setLoading(true)
    try {
      // let maxDate = selectedPeriod?.endDate
      // if (
      //   !maxDate &&
      //   selectedPeriod?.clientConsumptionPeriodType !==
      //     CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT &&
      //   selectedPeriod?.state === CLIENT_SPRINT_STATES.IN_PROGRESS
      // ) {
      //   const period =
      //     (
      //       selectedPeriod?.clientConsumptionPeriodType ===
      //       CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY
      //     ) ?
      //       "week"
      //     : "month"
      //   maxDate = DateTime.now().endOf(period)
      // }
      const response = await getClientReportBurnchartDataByClientIdAndPeriod(
        clientId,
        sprintId,
        period.toUpperCase()
      )
      if (!response.success) {
        throw new Error(response.message)
      }

      const result = response.result
      if (result?.length === 0) {
        setBurnchartData([])
        setEstimatedSprints(INITIAL_BURNCHART_ESTIMATED_SPRINTS)
        setLastSprint(null)
        setBurnchartState(INITIAL_BURNCHART_STATE)
        setPrevBurnchartState(INITIAL_BURNCHART_STATE)
        setMilestoneForChart(null)
        return
      }
      const lastSprint = result[result.length - 1]
      const isWeekly = result[0]?.weekNumber !== undefined
      const periodKeyTemp = isWeekly ? "weekNumber" : "sprintNumber"
      const lastPeriodNumber = lastSprint[periodKeyTemp]

      const averageKeyTemp =
        isWeekly ? "averagePointsPerWeek" : "averagePointsPerSprint"

      const hasPresentationData =
        sprintData?.semanasNominales != null &&
        sprintData?.semanasOptimistas != null &&
        sprintData?.semanasPesimistas != null

      const hasExtraData =
        extraData?.semanasNominales != null &&
        extraData?.semanasOptimistas != null &&
        extraData?.semanasPesimistas != null

      const milestoneSource =
        isPresentationMode ? sprintData?.milestone : extraData?.milestone

      const milestoneTotalPoints = Number(milestoneSource?.totalPoints)

      const lastCompletedPeriodNumber =
        result[result.length - 1]?.[periodKeyTemp]

      const milestoneNominalPeriod =
        milestoneSource?.nominalWeeks ?
          lastCompletedPeriodNumber + milestoneSource.nominalWeeks
        : null
      const milestoneOptimisticPeriod =
        milestoneSource?.optimisticWeeks ?
          lastCompletedPeriodNumber + milestoneSource.optimisticWeeks
        : null
      const milestonePessimisticPeriod =
        milestoneSource?.pessimisticWeeks ?
          lastCompletedPeriodNumber + milestoneSource.pessimisticWeeks
        : null

      let burnchartResult

      if (isPresentationMode && hasPresentationData) {
        burnchartResult = calculateBurnchartProjectionFromPrecalculated({
          realPeriods: result,
          lastPeriod: lastSprint,
          periodKey: periodKeyTemp,
          semanasNominales: sprintData.semanasNominales,
          semanasOptimistas: sprintData.semanasOptimistas,
          semanasPesimistas: sprintData.semanasPesimistas,
          milestonePeriods: {
            nominal: milestoneNominalPeriod,
            optimistic: milestoneOptimisticPeriod,
            pessimistic: milestonePessimisticPeriod,
            milestoneTotalPoints,
          },
        })
      } else if (!isPresentationMode && hasExtraData) {
        burnchartResult = calculateBurnchartProjectionFromPrecalculated({
          realPeriods: result,
          lastPeriod: lastSprint,
          periodKey: periodKeyTemp,
          semanasNominales: extraData.semanasNominales,
          semanasOptimistas: extraData.semanasOptimistas,
          semanasPesimistas: extraData.semanasPesimistas,
          milestonePeriods: {
            nominal: milestoneNominalPeriod,
            optimistic: milestoneOptimisticPeriod,
            pessimistic: milestonePessimisticPeriod,
            milestoneTotalPoints,
          },
        })
      } else {
        burnchartResult = calculateBurnchartProjectionGeneric({
          realPeriods: result,
          lastPeriod: lastSprint,
          periodKey: periodKeyTemp,
          averageKey: averageKeyTemp,
          milestonePeriods: {
            nominal: milestoneNominalPeriod,
            optimistic: milestoneOptimisticPeriod,
            pessimistic: milestonePessimisticPeriod,
            milestoneTotalPoints,
          },
        })
      }

      const {
        sprints: burnchartSprints,
        estimatedSprint,
        optimisticSprint,
        pessimisticSprint,
      } = burnchartResult

      const findExactPeriodWhenReachesPoints = (
        sprints,
        targetPoints,
        projectionKey,
        periodKey
      ) => {
        if (!Number.isFinite(targetPoints) || !sprints?.length) return null

        let prevSprint = null
        for (const sprint of sprints) {
          const projectedValue = sprint[projectionKey]
          if (projectedValue == null) {
            prevSprint = sprint
            continue
          }

          if (projectedValue >= targetPoints) {
            if (
              prevSprint &&
              prevSprint[projectionKey] != null &&
              prevSprint[projectionKey] < targetPoints
            ) {
              const x0 = prevSprint[periodKey]
              const y0 = prevSprint[projectionKey]
              const x1 = sprint[periodKey]
              const y1 = projectedValue

              const exactPeriod =
                x0 + ((targetPoints - y0) * (x1 - x0)) / (y1 - y0)

              return parseFloat(exactPeriod.toFixed(2))
            }
            return sprint[periodKey]
          }
          prevSprint = sprint
        }
        return null
      }

      const firstPeriodNumber = burnchartSprints[0]?.[periodKeyTemp]

      const milestonePeriodNumber =
        milestoneSource?.weekNumber ??
        getPeriodNumberFromDate({
          targetDate: milestoneSource?.nextImportantDate,
          periods: burnchartSprints,
          periodKey: periodKeyTemp,
        })

      const milestoneForChartValue =
        milestoneSource ?
          {
            ...milestoneSource,
            periodNumber: milestonePeriodNumber,
            nominalPeriod: milestoneNominalPeriod,
            optimisticPeriod: milestoneOptimisticPeriod,
            pessimisticPeriod: milestonePessimisticPeriod,
          }
        : null

      setMilestoneForChart(milestoneForChartValue)

      const averagePoints = lastSprint?.[averageKeyTemp]
      const totalPointsToComplete = lastSprint?.totalPointsToComplete
      const totalCompletedPoints = lastSprint?.totalCompletedPoints
      setBurnchartData(burnchartSprints)
      setEstimatedSprints({
        estimatedSprint,
        optimisticSprint,
        pessimisticSprint,
      })
      setLastSprint(lastSprint)
      setPrevBurnchartState({
        [averageKeyTemp]: averagePoints,
        totalPointsToComplete,
        totalCompletedPoints,
      })
      setBurnchartState({
        [averageKeyTemp]: averagePoints,
        totalPointsToComplete,
        totalCompletedPoints,
      })
    } catch (error) {
      setErrorMessage({
        title: "Error al cargar el burnchart",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }
  const {
    data: lineStyleData,
    handleMouseEnterLegend,
    handleMouseLeaveLegend,
  } = useHighlightLineChartData({
    lineLabels: [
      "Total",
      "Completed",
      "Projected",
      "ProjectedPessimistic",
      "ProjectedOptimistic",
    ],
    initialData: burnchartData,
  })
  const isBurnchartStateModified = useMemo(() => {
    return (
      !_.isEqual(burnchartState, prevBurnchartState) &&
      !Object.values(prevBurnchartState).every(
        (value) => value === null || value === undefined
      )
    )
  }, [burnchartState])

  const resetBurnchartState = () => {
    setBurnchartState(INITIAL_BURNCHART_STATE)
    setPrevBurnchartState(INITIAL_BURNCHART_STATE)
    setEstimatedSprints(INITIAL_BURNCHART_ESTIMATED_SPRINTS)
    setBurnchartData([])
    setLastSprint(null)
    fetchBurnchartData()
    setMilestoneForChart(null)
  }
  return (
    <>
      <ClientReportCard
        collapsible={collapsible}
        title={
          <SprintTitle
            lastSprint={lastSprint}
            mode={mode}
            closeType={selectedPeriod?.closeType}
          />
        }
        iconClassName={
          mode === CLIENT_SPRINT_CARD_MODES.EDITABLE &&
          `pi pi-paperclip text-3xl`
        }
        icon={<TrendingUpRoundedIcon style={{ fontSize: "32px" }} />}
        className="burnchart-card font-cairo w-full md:flex-shrink-0"
        headerButton={
          mode === CLIENT_SPRINT_CARD_MODES.EDITABLE && (
            <ResetButton
              onClick={resetBurnchartState}
              disabled={!isBurnchartStateModified || loading}
            />
          )
        }
      >
        <div className="burnchart-grid">
          <div ref={refChart} className="burnchart-chart">
            <ClientReportBurnchart
              data={burnchartData}
              estimatedSprints={estimatedSprints}
              lineStyleData={lineStyleData}
              loading={loading}
              mode={mode}
              milestone={milestoneForChart}
            />
          </div>
          <div className="burnchart-summary">
            <ClientReportBurnchartSummary
              setBurnchartState={setBurnchartState}
              burnchartState={burnchartState}
              estimatedSprints={estimatedSprints}
              handleMouseEnterLegend={handleMouseEnterLegend}
              handleMouseLeaveLegend={handleMouseLeaveLegend}
              milestone={milestoneForChart}
              loading={loading}
              mode={mode}
            />
          </div>
        </div>
      </ClientReportCard>
    </>
  )
}

const SprintTitle = ({ lastSprint, mode, closeType = null }) => {
  const isWeekly = lastSprint?.weekNumber !== undefined

  if (mode === CLIENT_SPRINT_CARD_MODES.VIEW_ONLY) {
    return "Burnchart"
  }

  const periodNumber =
    isWeekly ? lastSprint?.weekNumber : lastSprint?.sprintNumber
  const periodLabel = isWeekly ? "Semana" : "Sprint"

  if (!lastSprint || periodNumber === 0) {
    return (
      <div className="flex gap-2 align-items-center">
        <span>{periodLabel} 1</span>
        <span
          className="font-normal line-height-1"
          style={{
            fontSize: "16px",
          }}
        >
          Por finalizar
        </span>
      </div>
    )
  }

  const startDate = getValidLuxonDate(lastSprint.startDate)
  const endDate = getValidLuxonDate(lastSprint.endDate)

  return (
    <div className="flex gap-2 align-items-center">
      <span>
        {periodLabel}{" "}
        {closeType === CLOSE_TYPES.TIMEOUT ?
          CLOSE_TYPE_LABELS.TIMEOUT.toLowerCase()
        : periodNumber}
      </span>
      <span
        className="font-normal line-height-1"
        style={{
          fontSize: "16px",
        }}
      >
        {startDate?.toFormat("dd/MM/yyyy")} -{" "}
        {endDate?.toFormat("dd/MM/yyyy") || "En curso"}
      </span>
    </div>
  )
}

export default ClientBurnchart
